import { useGetAgents } from '@/pages/agents/api';
import { useAuth } from '@/services/auth';
import { AgentStatus, IAgent } from '@d-id/types';
import { useMemo } from 'react';

const agentTimeout = 1000 * 60 * 60 * 1.5;

const isPublicAgent = (agent: IAgent) => agent.access === 'public';

export const useAgentsLibrary = () => {
    const { isAnonymous } = useAuth();
    const { data, isLoading } = useGetAgents(isAnonymous);

    const allAgents =
        useMemo(
            () =>
                (data?.items || [])
                    .filter((item: IAgent) => item.status !== AgentStatus.Rejected)
                    .map((item: IAgent) =>
                        [AgentStatus.Started].includes(item.status as AgentStatus) &&
                        Date.now() - new Date(item.modified_at).getTime() > agentTimeout
                            ? { ...item, status: AgentStatus.Error }
                            : item
                    )
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
            [data]
        ) || [];

    const publicAgents = allAgents.filter(item => isPublicAgent(item));
    const privateAgents = allAgents.filter(item => !isPublicAgent(item));

    const embedAgents = privateAgents.filter(item => !!item.embed && (!item.use_case || item.use_case === 'd-id-agents'));
    const nonEmbedAgents = privateAgents.filter(item => !item.embed);

    return { embedAgents, nonEmbedAgents, publicAgents, privateAgents, isLoading };
};
